import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface ContentLoadingPlaceholderProps {
  icon: ReactNode;
  title: string;
  description: string;
}

const ContentLoadingPlaceholder = ({ icon, title, description }: ContentLoadingPlaceholderProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 5,
        px: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.action.hover,
        border: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ position: "relative", width: 56, height: 56, mb: 2 }}>
        <CircularProgress size={56} thickness={2} sx={{ color: theme.palette.primary.main }} />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
        {description}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        {t("This may take a few seconds depending on the file size")}
      </Typography>
    </Box>
  );
};

export default ContentLoadingPlaceholder;
